import * as React from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay';
import classnames from 'classnames';

// components
import { Hold } from './Hold';
import { PrivateOfferItemPrices } from './PrivateOfferItemPrices';
import { SaleItemPrices } from './SaleItemPrices';
import { NetItemPrices } from './NetItemPrices';
import { RetailItemPrices } from './RetailItemPrices';
import { getPriceMessagesTypes, textPriceValue } from './messages/priceMessages';
import SoldWordIcon from 'dibs-vg/dist/react/sold';

// styles
import styles from './PriceDisplay.scss';

// helpers
import get from 'lodash.get';

const SIZE_SMALL = 'small';
const SIZE_MEDIUM = 'medium';

const getPricing = ({ item, sku }) => {
    let pricingData = {};
    const lastTransaction = get(item, 'lastTransaction');
    if (lastTransaction) {
        pricingData.percentageOff = get(item, 'pricing.percentageOff');
        pricingData.retailPrice = get(lastTransaction, 'summary.retailPrice.amount');
        pricingData.saleOriginPrice = pricingData.retailPrice;
        pricingData.salePrice = get(lastTransaction, 'summary.salePrice.amount');
        pricingData.netPrice =
            get(lastTransaction, 'summary.netPrice.amount') || get(item, 'pricing.netPrice');
    } else if (sku) {
        pricingData = get(sku, 'pricing');
    } else {
        pricingData = get(item, 'pricing');
    }

    return pricingData;
};

const getDisplayInfo = ({ displayInfo, sku }) => {
    return sku ? get(sku, 'displayInfo.pricing') : get(displayInfo, 'pricing');
};

const getPrivateOfferFromTransaction = transaction =>
    get(transaction, 'isPrivateOffer') &&
    get(transaction, 'offers').find(offer => offer.canUseOfferForCheckout);

const getPrivateOffer = ({ item, sku, transactions }) => {
    let privateOffer;

    if (sku) {
        const edges = get(transactions, 'edges') || [];
        for (const edge of edges) {
            const node = get(edge, 'node');
            const skuId = get(node, 'skuId');

            if (skuId === sku.serviceId) {
                privateOffer = getPrivateOfferFromTransaction(node);
                break;
            }
        }
    } else {
        const transaction = get(item, 'lastTransaction');
        privateOffer = getPrivateOfferFromTransaction(transaction);
    }
    return privateOffer;
};

export function PriceDisplayComponent(props) {
    const { currency, item, page, textSize } = props;
    const holdDisplayTime = get(item, 'activeHold.displayTimeToExpire') || null;
    const pricing = getPricing(props);
    const pricingDisplayInfo = getDisplayInfo(props);
    const privateOffer = getPrivateOffer(props);
    const isPdp = page === 'pdp';
    const isPrivatePdp = page === 'pdp-private';

    if (!pricing || !pricingDisplayInfo) {
        return <div className={styles.wrapper} />;
    }

    // hold types include - ON_HOLD & ON_HOLD_CUSTOM
    // text price types include - sold, unavailable & pur
    const { holdType, textPriceType } = getPriceMessagesTypes(pricingDisplayInfo);
    const {
        showOriginal,
        showRetail,
        showSale,
        showNet,
        showListPriceLabel,
        quantityDisplay,
        showSold,
    } = pricingDisplayInfo;

    // Getting priceComponent
    let priceComponent = null;
    const priceComponentProps = {
        pricing,
        currency,
        showListPriceLabel,
        quantityDisplay,
    };

    if (privateOffer) {
        const privateOfferAmount = get(privateOffer, 'amountPerItem.convertedAmountList').find(
            amount => {
                return amount.currency === currency;
            }
        ).amount;

        priceComponent = (
            <PrivateOfferItemPrices
                {...priceComponentProps}
                amount={privateOfferAmount}
                expiration={privateOffer.expirationDate}
                showExpirationDate={!holdDisplayTime}
                showRetail={showRetail}
                showNet={showNet}
            />
        );
    } else if (!isPrivatePdp && showSale) {
        // Item is on sale
        priceComponent = <SaleItemPrices {...priceComponentProps} showOriginal={showOriginal} />;
    } else if (!isPrivatePdp && showNet) {
        // Item has a net price and is being viewed by a trade user
        priceComponent = <NetItemPrices {...priceComponentProps} showRetail={showRetail} />;
    } else if (showRetail) {
        // Item only has a retail price (or is being viewed by a non-trade user)
        priceComponent = <RetailItemPrices {...priceComponentProps} />;
    } else if (textPriceType) {
        // Item is unavailable or has no visible price (e.g. sold, unavailable or pur)
        priceComponent = (
            <div className={styles[textPriceType]} data-tn={`price-${textPriceType}-price`}>
                {isPdp && showSold ? (
                    <SoldWordIcon className={styles.icon} />
                ) : (
                    textPriceValue(textPriceType)
                )}
            </div>
        );
    }

    if (!priceComponent && !holdType) {
        return null;
    }

    const priceWrapperClasses = classnames(styles.wrapper, {
        [styles.smallText]: textSize === SIZE_SMALL,
    });

    return (
        <div className={priceWrapperClasses} data-tn="price-wrapper">
            {/* 'On Hold For You' OR 'On Hold' */}
            <Hold holdType={holdType} holdDisplayTime={holdDisplayTime} />
            {/* Price */}
            {priceComponent}
        </div>
    );
}

PriceDisplayComponent.defaultProps = {
    textSize: SIZE_MEDIUM,
};

PriceDisplayComponent.propTypes = {
    currency: PropTypes.string,
    displayInfo: PropTypes.object,
    hasUserId: PropTypes.bool,
    item: PropTypes.object.isRequired,
    page: PropTypes.string,
    userId: PropTypes.string,
    textSize: PropTypes.oneOf([SIZE_SMALL, SIZE_MEDIUM]),
};

export const PriceDisplay = createFragmentContainer(PriceDisplayComponent, {
    item: graphql`
        fragment PriceDisplay_item on Item
            @argumentDefinitions(
                hasUserId: { type: "Boolean", defaultValue: false }
                userId: { type: "String!", defaultValue: "" }
            ) {
            activeHold {
                displayTimeToExpire
            }
            pricing {
                percentageOff {
                    salePrice
                    netPrice
                }
                saleOriginPrice {
                    convertedAmountList {
                        currency
                        amount
                    }
                }
                retailPrice {
                    convertedAmountList {
                        currency
                        amount
                    }
                }
                salePrice {
                    convertedAmountList {
                        currency
                        amount
                    }
                }
                netPrice {
                    convertedAmountList {
                        currency
                        amount
                    }
                }
            }
            lastTransaction(buyerId: $userId) @include(if: $hasUserId) {
                summary {
                    retailPrice {
                        amount {
                            convertedAmountList {
                                currency
                                amount
                            }
                        }
                    }
                    salePrice {
                        amount {
                            convertedAmountList {
                                currency
                                amount
                            }
                        }
                    }
                    netPrice {
                        amount {
                            convertedAmountList {
                                currency
                                amount
                            }
                        }
                    }
                }
                isPrivateOffer
                offers {
                    canUseOfferForCheckout
                    expirationDate
                    amountPerItem {
                        convertedAmountList {
                            currency
                            amount
                        }
                    }
                }
            }
        }
    `,
    sku: graphql`
        fragment PriceDisplay_sku on ItemSkuType
            @argumentDefinitions(
                hasSkuId: { type: "Boolean", defaultValue: false }
                hasUserId: { type: "Boolean", defaultValue: false }
                userId: { type: "String!", defaultValue: "" }
                page: { type: "PageDisplayEnum", defaultValue: pdp }
            ) {
            ... @include(if: $hasSkuId) {
                serviceId
                pricing {
                    percentageOff {
                        salePrice
                        netPrice
                    }
                    saleOriginPrice {
                        convertedAmountList {
                            currency
                            amount
                        }
                    }
                    retailPrice {
                        convertedAmountList {
                            currency
                            amount
                        }
                    }
                    salePrice {
                        convertedAmountList {
                            currency
                            amount
                        }
                    }
                    netPrice {
                        convertedAmountList {
                            currency
                            amount
                        }
                    }
                }
                displayInfo(page: $page, buyerId: $userId) {
                    pricing {
                        quantityDisplay
                        showSold
                        showUnavailable
                        showPUR
                        showNet
                        showSale
                        showRetail
                        showOriginal
                        showOnHold
                        showOnHoldCustom
                        showListPriceLabel
                    }
                }
            }
        }
    `,
    displayInfo: graphql`
        fragment PriceDisplay_displayInfo on ItemDisplayInfo {
            pricing {
                quantityDisplay
                showSold
                showUnavailable
                showPUR
                showNet
                showSale
                showRetail
                showOriginal
                showOnHold
                showOnHoldCustom
                showListPriceLabel
            }
        }
    `,
    transactions: graphql`
        fragment PriceDisplay_transactions on TransactionConnection
            @argumentDefinitions(hasSkuId: { type: "Boolean", defaultValue: false }) {
            ... @include(if: $hasSkuId) {
                edges {
                    node {
                        skuId
                        isPrivateOffer
                        offers {
                            canUseOfferForCheckout
                            expirationDate
                            amountPerItem {
                                convertedAmountList {
                                    currency
                                    amount
                                }
                            }
                        }
                    }
                }
            }
        }
    `,
});
